import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    TextField,
    Tabs,
    Tab,
    Chip,
    Autocomplete,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Langs, { Localize } from "../../../app/lang/langs";
import { showSuccessToast, showErrorToast, safeParse } from "../../../app/utils/helpers";
import { generateInterviewPDF, getInterviews, statusOptions, updateInterview } from "../VentrSlice";
import { AssetsPannel } from "../../../components/inputs/AssetsPannel";
import MobileMissionHistoryModal from "./MobileMissionHistoryModal";
import { ConfirmModal } from "../../../components/popups/ConfirmModal";
import moment from "moment";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
    fontWeight: 600,
    mb: 0.75,
    letterSpacing: "-0.01em",
    color: "text.secondary",
    fontSize: "0.82rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.06)",
        backdropFilter: "blur(6px)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(0,0,0,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(0,0,0,0.18)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
        /* View-only fields use readOnly instead of disabled so their content
           stays scrollable and selectable — keep the disabled look here. */
        "&.Mui-readOnly": {
            opacity: 0.6,
            "&:hover fieldset, &.Mui-focused fieldset": {
                borderColor: "rgba(0,0,0,0.08)",
                boxShadow: "none",
            },
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& textarea": {
        fontSize: "16px",
    },
    "& input::placeholder, & textarea::placeholder": {
        opacity: 0.45,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.45)",
    boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Option constants                                                   */
/* ------------------------------------------------------------------ */
const yesOrNoOptions = [
    { label: "Oui", value: "Yes" },
    { label: "Non", value: "No" },
];

const levelOptions = [
    { label: "BAC +2", value: "BAC+2" },
    { label: "BAC +3", value: "BAC+3" },
    { label: "BAC +4", value: "BAC+4" },
    { label: "BAC +5", value: "BAC+5" },
    { label: "BAC +8", value: "BAC+8" },
    { label: "Autre", value: "Autre" },
];

const fundingOptions = [
    { label: "CPF", value: "CPF" },
    { label: "Employeur", value: "Employeur" },
    { label: "Autofinancé", value: "Autofinancé" },
    { label: "Autre", value: "Autre" },
];

const originsOptions = [
    { label: "Employeur", value: "Employeur" },
    { label: "Salarié", value: "Salarié" },
];

const locationsOptions = [
    { label: "Non", value: "No" },
    { label: "La garenne colombes (92)", value: "La garenne colombes (92)" },
    { label: "Mérignac (33)", value: "Mérignac (33)" },
    { label: "Beyrouth", value: "Beyrouth" },
    { label: "Ile Maurice", value: "Ile Maurice" },
];

const typeOptions = [
    { label: "2 ans", value: "2" },
    { label: "3 ans", value: "3" },
    { label: "6 ans", value: "6" },
];

/* ------------------------------------------------------------------ */
/*  Tab definitions                                                    */
/* ------------------------------------------------------------------ */
const ALL_TABS = [
    { id: "DIPLOMAS", label: "Diplômes" },
    { id: "PRE_VITAL_CAREER_PATH", label: "Avant VITAL" },
    { id: "VITAL_CAREER_PATH", label: "Parcours VITAL" },
    { id: "TRAINING_COMPLETED", label: "Formations" },
    { id: "PROFESSIONAL_PROJECT", label: "Projet pro" },
    { id: "DESIRED_TRAINING", label: "Form. souhaitées" },
    { id: "OTHER", label: "Autre" },
    { id: "bilan_6ans", label: "Bilan 6 ans", forType: "6" },
    { id: "FIN", label: "Finalisation" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileInterviewModal({ interview, setInterview, visible, setVisible }) {
    const dispatch = useDispatch();

    const allInterviews = useSelector((state) => state.ventr.interviewsList);
    const userId = useSelector((state) => state.login.userId);
    const idRole = useSelector((state) => state.login.userRoleId);
    const userPermissions = useSelector((state) => state.login.permissions);

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [historyModalVisibility, setHistoryModalVisibility] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);

    const [interviewLocal, setInterviewLocal] = useState({
        id: null,
        id_collab: null,
        status: "",
        type: "",
        theoretical_date: "",
        planned_date: "",
        diplomas: [],
        post_experience: [],
        vital_experience: [],
        training_completed: [],
        professional_project: [{
            desired_period: "",
            reorient: "",
            location: "",
            reorient_comments: "",
            responsibility: "",
            responsibility_comments: "",
            change_mission: "",
            comments: "",
            progressQ1: "",
            progressQ2: "",
            progressQ3: "",
        }],
        desired_training: [{
            period: "",
            name: "",
            funding: "",
            comments: "",
        }],
        bilan_6ans: [{
            before_last_interview_date: "",
            last_interview_date: "",
            q1: "",
            q2: "",
            q3: "",
            q4: "",
            q5: "",
            comments: "",
        }],
        collab_comment: "",
        rh_comment: "",
        interviewed_by: "",
        assets: [],
    });

    const isCollab = userId == interview?.id_collab;
    const isManager = idRole == 2;
    const mobileVentrPerms = Array.isArray(userPermissions)
        ? userPermissions.filter(p => p.scope === config.scopes.ventr)
        : [];
    const hasVentrViewOnlyPermission = mobileVentrPerms.some(p => p.type === config.permissionTypes.view);
    const isViewOnly = interview?.status === "done"
        || (isCollab && interview?.status !== "awaiting_completion")
        || (!isCollab && hasVentrViewOnlyPermission);

    const activeTabs = ALL_TABS.filter(
        (t) => !t.forType || (interviewLocal && interviewLocal.type == t.forType)
    );

    /* ------------------------------------------------------------------ */
    /*  Populate local state from prop                                     */
    /* ------------------------------------------------------------------ */
    function getBeforeLastInterviewDate(iv) {
        let date = "";
        let doneInterviews = allInterviews?.done?.filter(
            (i) => i.id_collab == iv.id_collab && i.planned_date != iv.planned_date
        );
        if (doneInterviews && doneInterviews[1] && doneInterviews[1].planned_date) {
            date = moment(doneInterviews[1].planned_date, "YYYY-MM-DD").format("YYYY-MM-DD");
        }
        return date;
    }

    function getLastInterviewDate(iv) {
        let date = "";
        let doneInterviews = allInterviews?.done?.filter(
            (i) => i.id_collab == iv.id_collab && i.planned_date != iv.planned_date
        );
        if (doneInterviews && doneInterviews[0] && doneInterviews[0].planned_date) {
            date = moment(doneInterviews[0].planned_date, "YYYY-MM-DD").format("YYYY-MM-DD");
        }
        return date;
    }

    useEffect(() => {
        if (interview) {
            try {
                setInterviewLocal({
                    id: interview.id || null,
                    id_collab: interview.id_collab || null,
                    status: interview.status || "",
                    type: interview.type || "",
                    theoretical_date: interview.theoretical_date || "",
                    planned_date: interview.planned_date || "",
                    diplomas: safeParse(interview.diplomas, []),
                    post_experience: safeParse(interview.post_experience, []),
                    vital_experience: safeParse(interview.vital_experience, []),
                    training_completed: safeParse(interview.training_completed, []),
                    professional_project: safeParse(interview.professional_project, [{
                        desired_period: "", reorient: "", location: "",
                        reorient_comments: "", responsibility: "",
                        responsibility_comments: "", change_mission: "",
                        comments: "", progressQ1: "", progressQ2: "", progressQ3: "",
                    }]),
                    desired_training: safeParse(interview.desired_training, [{
                        period: "", name: "", funding: "", comments: "",
                    }]),
                    bilan_6ans: safeParse(interview.bilan_6ans, [{
                        before_last_interview_date: getBeforeLastInterviewDate(interview),
                        last_interview_date: getLastInterviewDate(interview),
                        q1: "", q2: "", q3: "", q4: "", q5: "", comments: "",
                    }]),
                    collab_comment: interview.collab_comment || "",
                    rh_comment: interview.rh_comment || "",
                    interviewed_by: interview.interviewed_by || "",
                    assets: interview.assets || [],
                });
            } catch (error) {
                console.error("Error setting interview data:", error);
            }
        } else {
            setInterviewLocal({
                id: null, id_collab: null, status: "", type: "",
                theoretical_date: "", planned_date: "",
                diplomas: [], post_experience: [], vital_experience: [],
                training_completed: [], professional_project: [],
                desired_training: [], bilan_6ans: [],
                collab_comment: "", rh_comment: "", interviewed_by: "", assets: [],
            });
            setSelectedTabIndex(0);
        }
    }, [interview]);

    /* ------------------------------------------------------------------ */
    /*  Handlers                                                           */
    /* ------------------------------------------------------------------ */
    const onHide = () => {
        setVisible(false);
        setInterview(null);
        setSelectedTabIndex(0);
    };

    const handleSave = (iv, status) => {
        let mandatoryFields = {
            diplomas: [{ year: "string", diplome: "string", level: "string" }],
            post_experience: [{ from: "string", to: "string", poste: "string", enterprise: "string" }],
            vital_experience: [{ client: "string", from: "string", poste: "string" }],
            training_completed: [{
                start_date: "string", date: "string", name: "string",
                certificate_obtained: "string", assessment: "string",
                origin: "string", funding: "string", formation_vital: "string",
            }],
        };
        if (idRole > 1 && iv.type == "6") {
            mandatoryFields.bilan_6ans = [{
                q1: "string", q2: "string", q3: "string",
                q4: "string", q5: "string", comments: "string",
            }];
        }

        const hasInvalidFields = (array, expectedStructure) => {
            return array.some((item) =>
                Object.keys(expectedStructure).some(
                    (key) => item[key] === "" || (typeof item[key] === "string" && item[key].trim() === "")
                )
            );
        };

        for (const key in mandatoryFields) {
            if (iv[key] && Array.isArray(iv[key]) && iv[key].length > 0) {
                let expectedStructure = mandatoryFields[key][0];
                if (hasInvalidFields(iv[key], expectedStructure)) {
                    let message = Localize("MESSAGE_INTERVIEW_MODAL") + " " + Localize(key);
                    showErrorToast(message);
                    return;
                }
            }
        }

        let updatedInterviewLocal = {
            ...interviewLocal,
            theoretical_date: moment(iv.theoretical_date).format("YYYY-MM-DD HH:mm:ss"),
            planned_date:
                iv.planned_date && iv.planned_date.toString().trim() != ""
                    ? moment(iv.planned_date).format("YYYY-MM-DD HH:mm:ss")
                    : null,
        };

        if (status) {
            updatedInterviewLocal.status = status;
        }

        dispatch(updateInterview({ interview: updatedInterviewLocal })).then((response) => {
            const responseData = response.payload.responseData;
            if (responseData && responseData.status === 1) {
                showSuccessToast("Entretien mise à jour avec succès.");
                dispatch(getInterviews());
                if (status) onHide();
            } else {
                showErrorToast(
                    "La mise à jour de votre entretien a échoué. Veuillez compléter tous les détails puis réessayer."
                );
            }
        });
    };

    const handleFinishInterview = () => {
        setConfirmModal(false);
        handleSave(interviewLocal, "awaiting_scheduling");
    };

    const validatFields = (value, field) => {
        if (Array.isArray(interviewLocal.assets) && interviewLocal.assets.length > 0) {
            setInterviewLocal({ ...interviewLocal, [field]: value });
        } else {
            showErrorToast("Merci de télécharger le document d'entretien signé avant de valider.");
        }
    };

    const exportInterviewAsPdf = () => {
        if (interviewLocal.id) {
            let fileName = `${interviewLocal.id_collab}_${moment().format("YYYYMMDD_HHmmss")}.pdf`;
            if (interview) {
                fileName = `${interview.collab_prenom}_${interview.collab_nom}_${moment().format("YYYYMMDD_HHmmss")}.pdf`;
            }
            showSuccessToast("Génération du PDF en cours...");
            dispatch(generateInterviewPDF({ interviewId: interviewLocal.id, fileName }));
        }
    };

    /* ------------------------------------------------------------------ */
    /*  Array add/remove helpers                                           */
    /* ------------------------------------------------------------------ */
    const addDiploma = () => {
        setInterviewLocal({
            ...interviewLocal,
            diplomas: [...interviewLocal.diplomas, { year: "", diplome: "", level: "" }],
        });
    };

    const removeDiploma = (idx) => {
        let arr = [...interviewLocal.diplomas];
        arr.splice(idx, 1);
        setInterviewLocal({ ...interviewLocal, diplomas: arr });
    };

    const addPostExperience = () => {
        setInterviewLocal({
            ...interviewLocal,
            post_experience: [...interviewLocal.post_experience, { poste: "", enterprise: "", from: "", to: "" }],
        });
    };

    const removePostExperience = (idx) => {
        let arr = [...interviewLocal.post_experience];
        arr.splice(idx, 1);
        setInterviewLocal({ ...interviewLocal, post_experience: arr });
    };

    const addVitalExperience = () => {
        setInterviewLocal({
            ...interviewLocal,
            vital_experience: [...interviewLocal.vital_experience, { poste: "", client: "", from: "", to: "" }],
        });
    };

    const removeVitalExperience = (idx) => {
        let arr = [...interviewLocal.vital_experience];
        arr.splice(idx, 1);
        setInterviewLocal({ ...interviewLocal, vital_experience: arr });
    };

    const addTraining = () => {
        setInterviewLocal({
            ...interviewLocal,
            training_completed: [
                ...interviewLocal.training_completed,
                {
                    name: "", start_date: "", date: "", certificate_obtained: "",
                    assessment: "", origin: "", funding: "", formation_vital: "",
                },
            ],
        });
    };

    const removeTraining = (idx) => {
        let arr = [...interviewLocal.training_completed];
        arr.splice(idx, 1);
        setInterviewLocal({ ...interviewLocal, training_completed: arr });
    };

    /* ------------------------------------------------------------------ */
    /*  Generic field update for arrays                                    */
    /* ------------------------------------------------------------------ */
    const updateArrayItem = (arrayKey, index, field, value) => {
        let arr = [...interviewLocal[arrayKey]];
        arr[index] = { ...arr[index], [field]: value };
        setInterviewLocal({ ...interviewLocal, [arrayKey]: arr });
    };

    const updateProject = (field, value) => {
        let arr = [...interviewLocal.professional_project];
        arr[0] = { ...arr[0], [field]: value };
        setInterviewLocal({ ...interviewLocal, professional_project: arr });
    };

    const updateDesiredTraining = (field, value) => {
        let arr = [...interviewLocal.desired_training];
        arr[0] = { ...arr[0], [field]: value };
        setInterviewLocal({ ...interviewLocal, desired_training: arr });
    };

    const updateBilan = (field, value) => {
        let arr = [...interviewLocal.bilan_6ans];
        arr[0] = { ...arr[0], [field]: value };
        setInterviewLocal({ ...interviewLocal, bilan_6ans: arr });
    };

    /* ------------------------------------------------------------------ */
    /*  Section header with add button                                     */
    /* ------------------------------------------------------------------ */
    const SectionHeader = ({ title, onAdd }) => (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", letterSpacing: "-0.01em" }}>
                {title}
            </Typography>
            {onAdd && !isViewOnly && (
                <IconButton
                    onClick={onAdd}
                    sx={{
                        bgcolor: "rgba(255,144,21,0.1)",
                        color: "rgb(255,144,21)",
                        transition: TRANSITION_SMOOTH,
                        "&:hover": { bgcolor: "rgba(255,144,21,0.2)", transform: "scale(1.08)" },
                        "&:active": { transform: "scale(0.95)" },
                    }}
                >
                    <AddIcon />
                </IconButton>
            )}
        </Box>
    );

    /* ------------------------------------------------------------------ */
    /*  Delete button for list items                                       */
    /* ------------------------------------------------------------------ */
    const DeleteButton = ({ onClick }) => (
        <IconButton
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            size="small"
            sx={{
                color: "#d32f2f",
                transition: TRANSITION_SMOOTH,
                "&:hover": { bgcolor: "rgba(211,47,47,0.08)", transform: "scale(1.1)" },
                "&:active": { transform: "scale(0.92)" },
            }}
        >
            <DeleteOutlineIcon fontSize="small" />
        </IconButton>
    );

    /* ------------------------------------------------------------------ */
    /*  MUI Select helper (Autocomplete dropdown)                          */
    /* ------------------------------------------------------------------ */
    const MobileSelect = ({ label, value, options, onChange, disabled, required, freeSolo }) => (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant="body2" sx={fieldLabelSx}>
                    {label} {required && <span style={{ color: "red" }}>*</span>}
                </Typography>
            )}
            <Autocomplete
                freeSolo={freeSolo}
                options={options}
                getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.label || ""
                }
                value={
                    freeSolo
                        ? (options.find((o) => o.value === value) || (value ? { label: value, value } : null))
                        : (options.find((o) => o.value === value) || null)
                }
                onChange={(_, newVal) => {
                    if (typeof newVal === "string") onChange(newVal);
                    else onChange(newVal ? newVal.value : "");
                }}
                onInputChange={freeSolo ? (_, newInput, reason) => {
                    if (reason === "input") onChange(newInput);
                } : undefined}
                disabled={disabled}
                isOptionEqualToValue={(option, val) => option.value === val?.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Sélectionner"
                        sx={glassmorphicTextFieldSx}
                    />
                )}
                slotProps={{
                    popper: { sx: { zIndex: 1500 } },
                }}
            />
        </Box>
    );

    /* ------------------------------------------------------------------ */
    /*  Accordion item style                                               */
    /* ------------------------------------------------------------------ */
    const accordionSx = {
        ...glassCardSx,
        "&:before": { display: "none" },
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    };

    /* ------------------------------------------------------------------ */
    /*  Tab content renderers                                              */
    /* ------------------------------------------------------------------ */

    /* DIPLOMAS */
    const renderDiplomas = () => (
        <Box>
            <SectionHeader title={Localize("DIPLOMAS")} onAdd={addDiploma} />
            {interviewLocal.diplomas.length === 0 && (
                <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2 }}>
                    Aucune donnée
                </Typography>
            )}
            {interviewLocal.diplomas.map((item, index) => (
                <Accordion key={"diploma_" + index} defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {item.diplome || `Diplôme ${index + 1}`}
                            </Typography>
                            {!isViewOnly && <DeleteButton onClick={() => removeDiploma(index)} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="YEAR" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="number"
                            fullWidth
                            value={item.year || ""}
                            onChange={(e) => updateArrayItem("diplomas", index, "year", e.target.value)}
                            disabled={isViewOnly}
                            placeholder="ex: 2020"
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="DIPLOME" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.diplome || ""}
                            onChange={(e) => updateArrayItem("diplomas", index, "diplome", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <MobileSelect
                            label={Localize("EDUCATION_LEVEL")}
                            value={item.level}
                            options={levelOptions}
                            onChange={(val) => updateArrayItem("diplomas", index, "level", val)}
                            disabled={isViewOnly}
                            required
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );

    /* PRE VITAL CAREER PATH */
    const renderPreVitalCareerPath = () => (
        <Box>
            <SectionHeader title={Localize("PRE_VITAL_CAREER_PATH")} onAdd={addPostExperience} />
            {interviewLocal.post_experience.length === 0 && (
                <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2 }}>
                    Aucune donnée
                </Typography>
            )}
            {interviewLocal.post_experience.map((item, index) => (
                <Accordion key={"post_exp_" + index} defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {item.poste || `Expérience ${index + 1}`}
                            </Typography>
                            {!isViewOnly && <DeleteButton onClick={() => removePostExperience(index)} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="POSTE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.poste || ""}
                            onChange={(e) => updateArrayItem("post_experience", index, "poste", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="COMPANY" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.enterprise || ""}
                            onChange={(e) => updateArrayItem("post_experience", index, "enterprise", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="START_DATE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.from || ""}
                            onChange={(e) => updateArrayItem("post_experience", index, "from", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="END_DATE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.to || ""}
                            onChange={(e) => updateArrayItem("post_experience", index, "to", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );

    /* VITAL CAREER PATH */
    const renderVitalCareerPath = () => (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", letterSpacing: "-0.01em" }}>
                    {Localize("VITAL_CAREER_PATH")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                        onClick={() => setHistoryModalVisibility(true)}
                        sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                        }}
                    >
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                    {!isViewOnly && (
                        <IconButton
                            onClick={addVitalExperience}
                            sx={{
                                bgcolor: "rgba(255,144,21,0.1)",
                                color: "rgb(255,144,21)",
                                transition: TRANSITION_SMOOTH,
                                "&:hover": { bgcolor: "rgba(255,144,21,0.2)", transform: "scale(1.08)" },
                                "&:active": { transform: "scale(0.95)" },
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>
            {interviewLocal.vital_experience.length === 0 && (
                <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2 }}>
                    Aucune donnée
                </Typography>
            )}
            {interviewLocal.vital_experience.map((item, index) => (
                <Accordion key={"vital_exp_" + index} defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {item.poste || `Mission ${index + 1}`}
                            </Typography>
                            {!isViewOnly && <DeleteButton onClick={() => removeVitalExperience(index)} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="POSTE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.poste || ""}
                            onChange={(e) => updateArrayItem("vital_experience", index, "poste", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="CLIENT" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.client || ""}
                            onChange={(e) => updateArrayItem("vital_experience", index, "client", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="START_DATE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.from || ""}
                            onChange={(e) => updateArrayItem("vital_experience", index, "from", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="END_DATE" />
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.to || ""}
                            onChange={(e) => updateArrayItem("vital_experience", index, "to", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );

    /* TRAINING COMPLETED */
    const renderTrainingCompleted = () => (
        <Box>
            <SectionHeader title={Localize("TRAINING_COMPLETED")} onAdd={addTraining} />
            {interviewLocal.training_completed.length === 0 && (
                <Typography sx={{ color: "text.secondary", fontStyle: "italic", mb: 2 }}>
                    Aucune donnée
                </Typography>
            )}
            {interviewLocal.training_completed.map((item, index) => (
                <Accordion key={"training_" + index} defaultExpanded sx={accordionSx}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {item.name || `Formation ${index + 1}`}
                            </Typography>
                            {!isViewOnly && <DeleteButton onClick={() => removeTraining(index)} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="NAME_NAME" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.name || ""}
                            onChange={(e) => updateArrayItem("training_completed", index, "name", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="START_DATE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.start_date || ""}
                            onChange={(e) => updateArrayItem("training_completed", index, "start_date", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="END_DATE" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            type="date"
                            fullWidth
                            value={item.date || ""}
                            onChange={(e) => updateArrayItem("training_completed", index, "date", e.target.value)}
                            disabled={isViewOnly}
                            sx={glassmorphicTextFieldSx}
                        />

                        <MobileSelect
                            label={Localize("ORIGIN_TRAINING")}
                            value={item.origin}
                            options={originsOptions}
                            onChange={(val) => updateArrayItem("training_completed", index, "origin", val)}
                            disabled={isViewOnly}
                            required
                        />

                        <MobileSelect
                            label={Localize("FUNDING")}
                            value={item.funding}
                            options={fundingOptions}
                            onChange={(val) => updateArrayItem("training_completed", index, "funding", val)}
                            disabled={isViewOnly}
                            required
                        />

                        <MobileSelect
                            label={Localize("CERTIFICATE_OBTAINED")}
                            value={item.certificate_obtained}
                            options={yesOrNoOptions}
                            onChange={(val) => updateArrayItem("training_completed", index, "certificate_obtained", val)}
                            disabled={isViewOnly}
                            required
                        />

                        <MobileSelect
                            label={Localize("FORMATION_SUIVIE_VITAL")}
                            value={item.formation_vital}
                            options={yesOrNoOptions}
                            onChange={(val) => updateArrayItem("training_completed", index, "formation_vital", val)}
                            disabled={isViewOnly}
                            required
                        />

                        <Typography variant="body2" sx={fieldLabelSx}>
                            <Langs str="ASSESSMENT" /> <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            value={item.assessment || ""}
                            onChange={(e) => updateArrayItem("training_completed", index, "assessment", e.target.value)}
                            InputProps={{ readOnly: isViewOnly }}
                            sx={glassmorphicTextFieldSx}
                        />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );

    /* PROFESSIONAL PROJECT */
    const renderProfessionalProject = () => {
        const proj = interviewLocal.professional_project[0] || {};
        return (
            <Box>
                {/* Professional project section */}
                <Box sx={glassCardSx}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
                        {Localize("PROFESSIONAL_PROJECT")}
                    </Typography>

                    <MobileSelect
                        label={Localize("SPECIFY_LOCATION_LABEL")}
                        value={proj.location}
                        options={locationsOptions}
                        onChange={(val) => updateProject("location", val)}
                        disabled={isViewOnly}
                        freeSolo
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="DESIRED_PERIOD" />
                    </Typography>
                    <TextField
                        fullWidth
                        value={proj.desired_period || ""}
                        onChange={(e) => updateProject("desired_period", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />

                    <MobileSelect
                        label={Localize("REORIENT_LABEL")}
                        value={proj.reorient}
                        options={yesOrNoOptions}
                        onChange={(val) => updateProject("reorient", val)}
                        disabled={isViewOnly}
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="Comments" /> (réorientation)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.reorient_comments || ""}
                        onChange={(e) => updateProject("reorient_comments", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />

                    <MobileSelect
                        label={Localize("RESPONSIBILITY_LABEL")}
                        value={proj.responsibility}
                        options={yesOrNoOptions}
                        onChange={(val) => updateProject("responsibility", val)}
                        disabled={isViewOnly}
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="Comments" /> (responsabilité)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.responsibility_comments || ""}
                        onChange={(e) => updateProject("responsibility_comments", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />

                    <MobileSelect
                        label={Localize("CHANGE_FUNCTION_LABEL")}
                        value={proj.change_mission}
                        options={yesOrNoOptions}
                        onChange={(val) => updateProject("change_mission", val)}
                        disabled={isViewOnly}
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="Comments" /> (changement mission)
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.comments || ""}
                        onChange={(e) => updateProject("comments", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />
                </Box>

                {/* Progression section */}
                <Box sx={glassCardSx}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
                        <Langs str="PROGRESSION_DEPUIS_DERNIER_ENTRETIEN" />
                    </Typography>

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="PROGRESSION_DEPUIS_DERNIER_ENTRETIEN_Q1" />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.progressQ1 || ""}
                        onChange={(e) => updateProject("progressQ1", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="PROGRESSION_DEPUIS_DERNIER_ENTRETIEN_Q2" />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.progressQ2 || ""}
                        onChange={(e) => updateProject("progressQ2", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />

                    <Typography variant="body2" sx={fieldLabelSx}>
                        <Langs str="PROGRESSION_DEPUIS_DERNIER_ENTRETIEN_Q3" />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={proj.progressQ3 || ""}
                        onChange={(e) => updateProject("progressQ3", e.target.value)}
                        InputProps={{ readOnly: isViewOnly }}
                        sx={glassmorphicTextFieldSx}
                    />
                </Box>
            </Box>
        );
    };

    /* DESIRED TRAINING */
    const renderDesiredTraining = () => {
        const dt = interviewLocal.desired_training[0] || {};
        return (
            <Box sx={glassCardSx}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
                    {Localize("DESIRED_TRAINING")}
                </Typography>

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="DESIRED_PERIOD" />
                </Typography>
                <TextField
                    fullWidth
                    value={dt.period || ""}
                    onChange={(e) => updateDesiredTraining("period", e.target.value)}
                    InputProps={{ readOnly: isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="NAME_NAME" />
                </Typography>
                <TextField
                    fullWidth
                    value={dt.name || ""}
                    onChange={(e) => updateDesiredTraining("name", e.target.value)}
                    InputProps={{ readOnly: isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />

                <MobileSelect
                    label={Localize("FUNDING")}
                    value={dt.funding}
                    options={fundingOptions}
                    onChange={(val) => updateDesiredTraining("funding", val)}
                    disabled={isViewOnly}
                />

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="Comments" />
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={dt.comments || ""}
                    onChange={(e) => updateDesiredTraining("comments", e.target.value)}
                    InputProps={{ readOnly: isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />
            </Box>
        );
    };

    /* OTHER */
    const renderOthers = () => (
        <Box sx={glassCardSx}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
                {Localize("OTHER")}
            </Typography>

            <Typography variant="body2" sx={fieldLabelSx}>
                Date d'ancienneté
            </Typography>
            <Typography sx={{ mb: 2, fontSize: "0.95rem" }}>
                {interview && interview.anciennetee_date
                    ? moment(interview.anciennetee_date).format("DD-MM-YYYY")
                    : "N/A"}
            </Typography>

            <Typography variant="body2" sx={fieldLabelSx}>
                <Langs str="INTERVIEW_THEORETICAL_DATE" />
            </Typography>
            <TextField
                type="datetime-local"
                fullWidth
                value={
                    interviewLocal.theoretical_date
                        ? moment(interviewLocal.theoretical_date).format("YYYY-MM-DDTHH:mm")
                        : ""
                }
                onChange={(e) =>
                    setInterviewLocal({
                        ...interviewLocal,
                        theoretical_date: moment(e.target.value).format("YYYY-MM-DD HH:mm"),
                    })
                }
                disabled={isCollab || isManager || isViewOnly}
                sx={glassmorphicTextFieldSx}
            />

            <Typography variant="body2" sx={fieldLabelSx}>
                <Langs str="INTERVIEW_PLANNED_DATE" />
            </Typography>
            <TextField
                type="datetime-local"
                fullWidth
                value={
                    interviewLocal.planned_date
                        ? moment(interviewLocal.planned_date).format("YYYY-MM-DDTHH:mm")
                        : ""
                }
                onChange={(e) =>
                    setInterviewLocal({
                        ...interviewLocal,
                        planned_date: moment(e.target.value).format("YYYY-MM-DD HH:mm"),
                    })
                }
                disabled={isCollab || isManager || isViewOnly}
                sx={glassmorphicTextFieldSx}
            />

            <MobileSelect
                label={Localize("TYPE")}
                value={interviewLocal.type}
                options={typeOptions}
                onChange={(val) => setInterviewLocal({ ...interviewLocal, type: val })}
                disabled={isCollab || isManager || isViewOnly}
                required
            />
        </Box>
    );

    /* BILAN 6 ANS */
    const renderBilan6ans = () => {
        const bilan = interviewLocal.bilan_6ans[0] || {};
        return (
            <Box sx={glassCardSx}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
                    {Localize("bilan_6ans")}
                </Typography>

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="before_last_interview_date" />
                </Typography>
                <TextField
                    type="date"
                    fullWidth
                    value={bilan.before_last_interview_date || ""}
                    onChange={(e) => updateBilan("before_last_interview_date", e.target.value)}
                    disabled={isViewOnly}
                    sx={glassmorphicTextFieldSx}
                />

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="last_interview_date" />
                </Typography>
                <TextField
                    type="date"
                    fullWidth
                    value={bilan.last_interview_date || ""}
                    onChange={(e) => updateBilan("last_interview_date", e.target.value)}
                    disabled={isViewOnly}
                    sx={glassmorphicTextFieldSx}
                />

                {["q1", "q2", "q3", "q4", "q5"].map((qKey) => (
                    <MobileSelect
                        key={qKey}
                        label={Localize(`bilan_6ans_${qKey}`)}
                        value={bilan[qKey]}
                        options={yesOrNoOptions}
                        onChange={(val) => updateBilan(qKey, val)}
                        disabled={isViewOnly}
                        required
                    />
                ))}

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="bilan_comments" /> <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={5}
                    value={bilan.comments || ""}
                    onChange={(e) => updateBilan("comments", e.target.value)}
                    InputProps={{ readOnly: isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />
            </Box>
        );
    };

    /* FIN (Finalisation) */
    const renderFin = () => (
        <Box>
            <Box sx={glassCardSx}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                        {Localize("FIN")}
                    </Typography>
                    {(!isCollab && !isManager) && (
                        <IconButton
                            onClick={exportInterviewAsPdf}
                            sx={{
                                bgcolor: "rgba(255,144,21,0.1)",
                                color: "rgb(255,144,21)",
                                transition: TRANSITION_SMOOTH,
                                "&:hover": { bgcolor: "rgba(255,144,21,0.2)" },
                            }}
                        >
                            <PictureAsPdfIcon />
                        </IconButton>
                    )}
                </Box>

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="RH_COMMENT" />
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={interviewLocal.rh_comment || ""}
                    onChange={(e) =>
                        setInterviewLocal({ ...interviewLocal, rh_comment: e.target.value })
                    }
                    InputProps={{ readOnly: isCollab || isManager || isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="COLLAB_COMMENT" />
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={interviewLocal.collab_comment || ""}
                    onChange={(e) =>
                        setInterviewLocal({ ...interviewLocal, collab_comment: e.target.value })
                    }
                    InputProps={{ readOnly: isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />

                <Typography variant="body2" sx={fieldLabelSx}>
                    <Langs str="INTERVIEWED_BY" />
                </Typography>
                <TextField
                    fullWidth
                    value={interviewLocal.interviewed_by || ""}
                    onChange={(e) =>
                        setInterviewLocal({ ...interviewLocal, interviewed_by: e.target.value })
                    }
                    InputProps={{ readOnly: isCollab || isManager || isViewOnly }}
                    sx={glassmorphicTextFieldSx}
                />

                <MobileSelect
                    label={Localize("Status")}
                    value={interviewLocal.status}
                    options={statusOptions}
                    onChange={(val) => setInterviewLocal({ ...interviewLocal, status: val })}
                    disabled={isCollab || isManager || isViewOnly}
                />
            </Box>

            {/* Assets panel */}
            <Box sx={glassCardSx}>
                <AssetsPannel
                    tableName="interviews"
                    idParent={interviewLocal?.id}
                    assets={interviewLocal?.assets}
                    uploadCallBackFunction={() => {}}
                    deleteCallBackFunction={() => {}}
                    label="Signed files"
                    readOnly={isCollab || isManager || isViewOnly}
                    limitLength={10}
                    parentSetter={setInterviewLocal}
                />
            </Box>
        </Box>
    );

    /* ------------------------------------------------------------------ */
    /*  Tab content switch                                                 */
    /* ------------------------------------------------------------------ */
    const renderTabContent = () => {
        const currentTab = activeTabs[selectedTabIndex];
        if (!currentTab) return null;

        switch (currentTab.id) {
            case "DIPLOMAS":
                return renderDiplomas();
            case "PRE_VITAL_CAREER_PATH":
                return renderPreVitalCareerPath();
            case "VITAL_CAREER_PATH":
                return renderVitalCareerPath();
            case "TRAINING_COMPLETED":
                return renderTrainingCompleted();
            case "PROFESSIONAL_PROJECT":
                return renderProfessionalProject();
            case "DESIRED_TRAINING":
                return renderDesiredTraining();
            case "OTHER":
                return renderOthers();
            case "bilan_6ans":
                return renderBilan6ans();
            case "FIN":
                return renderFin();
            default:
                return null;
        }
    };

    /* ------------------------------------------------------------------ */
    /*  Status chip color                                                  */
    /* ------------------------------------------------------------------ */
    const getStatusColor = () => {
        const opt = statusOptions.find((o) => o.value === interviewLocal.status);
        return opt ? opt.color : "#666";
    };

    const getStatusLabel = () => {
        const opt = statusOptions.find((o) => o.value === interviewLocal.status);
        return opt ? opt.label : interviewLocal.status;
    };

    /* ------------------------------------------------------------------ */
    /*  Render                                                             */
    /* ------------------------------------------------------------------ */
    return (
        <>
            <Dialog
                fullScreen
                open={!!visible}
                onClose={onHide}
                TransitionComponent={Transition}
                sx={{
                    zIndex: 1400,
                    "& .MuiDialog-paper": {
                        background: "var(--surface-ground, #f4f6f8)",
                    },
                }}
            >
                {/* Sticky AppBar */}
                <AppBar
                    sx={{
                        position: "sticky",
                        top: 0,
                        bgcolor: "rgba(255,255,255,0.82)",
                        color: "text.primary",
                        boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                    }}
                >
                    <Toolbar sx={{ py: 0.5 }}>
                        <IconButton
                            edge="start"
                            onClick={onHide}
                            aria-label="close"
                            sx={{
                                transition: TRANSITION_SMOOTH,
                                borderRadius: "12px",
                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.06)",
                                    transform: "scale(1.08)",
                                },
                                "&:active": { transform: "scale(0.95)" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Box sx={{ ml: 1.5, flex: 1 }}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    letterSpacing: "-0.02em",
                                    lineHeight: 1.3,
                                }}
                            >
                                {interview?.collab_prenom} {interview?.collab_nom}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                                    Entretien {interviewLocal.type} ans
                                </Typography>
                                {interviewLocal.status && (
                                    <Chip
                                        label={getStatusLabel()}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: "0.68rem",
                                            fontWeight: 700,
                                            bgcolor: getStatusColor() + "18",
                                            color: getStatusColor(),
                                            border: `1px solid ${getStatusColor()}40`,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                        {(!isCollab && !isManager) && (
                            <IconButton
                                onClick={exportInterviewAsPdf}
                                sx={{
                                    transition: TRANSITION_SMOOTH,
                                    color: "rgb(255,144,21)",
                                    "&:hover": { bgcolor: "rgba(255,144,21,0.08)" },
                                }}
                            >
                                <PictureAsPdfIcon />
                            </IconButton>
                        )}
                    </Toolbar>

                    {/* Scrollable Tabs */}
                    <Tabs
                        value={selectedTabIndex}
                        onChange={(_, newVal) => setSelectedTabIndex(newVal)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            minHeight: 40,
                            px: 1,
                            "& .MuiTab-root": {
                                minHeight: 40,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.78rem",
                                py: 0.5,
                                minWidth: "auto",
                                px: 1.5,
                            },
                            "& .Mui-selected": {
                                color: "rgb(255,144,21)",
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: "rgb(255,144,21)",
                                height: 3,
                                borderRadius: "3px 3px 0 0",
                            },
                        }}
                    >
                        {activeTabs.map((tab) => (
                            <Tab key={tab.id} label={tab.label} />
                        ))}
                    </Tabs>
                </AppBar>

                {/* Scrollable Body */}
                <Box
                    sx={{
                        p: 2,
                        pb: 16,
                        mt: 0,
                        overflowY: "auto",
                        flex: 1,
                        "@keyframes fadeSlideIn": {
                            from: { opacity: 0, transform: "translateY(10px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s cubic-bezier(0, 0, 0.2, 1) both",
                    }}
                >
                    {isViewOnly && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: "12px",
                                bgcolor: "rgba(255,144,21,0.08)",
                                border: "1px solid rgba(255,144,21,0.2)",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <CheckCircleIcon sx={{ fontSize: 18, color: "rgb(255,144,21)" }} />
                            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                                Mode consultation uniquement
                            </Typography>
                        </Box>
                    )}
                    {renderTabContent()}
                </Box>

                {/* Sticky bottom bar */}
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        bgcolor: "rgba(255,255,255,0.78)",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",
                        boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
                        borderTop: "1px solid rgba(255,255,255,0.5)",
                        zIndex: 1,
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                    }}
                >
                    {/* Previous button */}
                    <IconButton
                        disabled={selectedTabIndex === 0}
                        onClick={() => setSelectedTabIndex((prev) => Math.max(0, prev - 1))}
                        sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                            "&:disabled": { opacity: 0.3 },
                        }}
                    >
                        <NavigateBeforeIcon />
                    </IconButton>

                    {/* Save / Finish buttons */}
                    {!isViewOnly && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleSave(interviewLocal)}
                            sx={{
                                minHeight: 48,
                                borderRadius: BORDER_RADIUS_BUTTON,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                bgcolor: "rgb(255,144,21)",
                                boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
                                transition: TRANSITION_SMOOTH,
                                "&:hover": {
                                    bgcolor: "rgb(230,125,10)",
                                    transform: "scale(1.02)",
                                    boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
                                },
                                "&:active": { transform: "scale(0.98)" },
                            }}
                        >
                            <Langs str="SAVE" />
                        </Button>
                    )}

                    {/* Finish button (collab or manager on last tab) */}
                    {(isCollab || isManager) &&
                        !isViewOnly &&
                        selectedTabIndex === activeTabs.length - 1 && (
                            <Button
                                variant="outlined"
                                onClick={() => setConfirmModal(true)}
                                sx={{
                                    minHeight: 48,
                                    borderRadius: BORDER_RADIUS_BUTTON,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    borderColor: "#d32f2f",
                                    color: "#d32f2f",
                                    transition: TRANSITION_SMOOTH,
                                    whiteSpace: "nowrap",
                                    px: 3,
                                    "&:hover": {
                                        borderColor: "#d32f2f",
                                        bgcolor: "rgba(211,47,47,0.04)",
                                    },
                                    "&:active": { transform: "scale(0.98)" },
                                }}
                            >
                                <Langs str="Finish" />
                            </Button>
                        )}

                    {/* Next button */}
                    <IconButton
                        disabled={selectedTabIndex >= activeTabs.length - 1}
                        onClick={() =>
                            setSelectedTabIndex((prev) => Math.min(activeTabs.length - 1, prev + 1))
                        }
                        sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            transition: TRANSITION_SMOOTH,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                            "&:disabled": { opacity: 0.3 },
                        }}
                    >
                        <NavigateNextIcon />
                    </IconButton>
                </Box>
            </Dialog>

            {/* Mission History Modal (reused from desktop) */}
            <MobileMissionHistoryModal
                visible={historyModalVisibility}
                setVisible={setHistoryModalVisibility}
                collabId={interviewLocal.id_collab}
            />

            {/* Confirm Finish Modal */}
            <ConfirmModal
                show={confirmModal}
                title={<Langs str="ADD_SHIFT" />}
                message={<Langs str="CONFIRM_FINISH_INTERVIEW_MSG" />}
                onConfirm={handleFinishInterview}
                onHide={() => setConfirmModal(false)}
            />
        </>
    );
}
